/* eslint-disable @typescript-eslint/no-explicit-any */
import { ActivatedRouteSnapshot } from '@angular/router';
import { VueLocaleService, VueTranslationService, WINDOW } from '@vue/vueframework';
import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { SessionDataService } from '../services/session-data.service';

@Injectable({
  providedIn: 'root'
})

/**
 * Resolver to get translations before component
 */
export class LocaleResolve {
  /**
   * Constructor of LocaleResolve to inject service.
   * @param sessionDataService The service used to store helpful session information
   * @param vueTranslationService The service used to fetch translations
   * @param vueLocaleService Service to update locale for translations
   * @param vueWindow Window containing DOM elements
   */
  public constructor(
    private sessionDataService: SessionDataService,
    private vueTranslationService: VueTranslationService,
    private vueLocaleService: VueLocaleService,
    @Inject(WINDOW) private vueWindow: Window,
  ) {}

  /**
   * Retrieves translation data for components.
   * If the locale in queryParams does not match the cookie's locale we need to reload to get translations.
   * @param route The Information contained in  route
   * @returns translations The translated messages for ConnectionCentral bundle.
   */
  public resolve(route: ActivatedRouteSnapshot): Observable<any> {
    const localeParam = route.queryParamMap.get('locale');

    if (
      localeParam &&
      this.isCookieLocaleDifferentFrom(localeParam)
    ) {
      if (this.isLocaleProperFormat(localeParam)) {
        this.vueLocaleService.setLocale(localeParam, true);
        this.sessionDataService.setLocale(localeParam);
        this.vueWindow.location.reload();
      } else {
        this.setLocaleToUSEnglish();
        this.removeLocaleQueryParameter();
      }
    } else if (
      !localeParam &&
      this.vueLocaleService.getLocaleFromCookie() &&
      this.isCookieLocaleDifferentFrom('en-us')
    ) {
      // Set to en_US if no locale queryParam, but non en-us locale found in cookies.
      this.setLocaleToUSEnglish();
      this.vueWindow.location.reload();
    }
    return of(this.sessionDataService.getLocale());
  }

  private isCookieLocaleDifferentFrom(localeString: string): boolean {
    return this.vueLocaleService.getLocaleFromCookie() !== this.vueLocaleService.normalizeLocale(localeString);
  }

  private isLocaleProperFormat(localeString: string): boolean {
    return localeString.indexOf('_') === 2 || localeString.toLocaleLowerCase() === 'cy';
  }

  private removeLocaleQueryParameter(): void {
    const location = this.vueWindow.location;
    const search: URLSearchParams = new URLSearchParams(location.search);
    if (search.get('locale')) {
      search.set('locale', this.sessionDataService.getLocale());
    }
    location.replace(location.pathname.concat('?', search.toString()));
  }

  private setLocaleToUSEnglish(): void {
    this.vueLocaleService.setLocale('en-us', true);
    this.sessionDataService.setLocale('en_US');
  }
}
